/* eslint-disable react/jsx-no-bind */
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import { useNavigate } from "react-router-dom";
import { TableView } from "~/components/build/Show/components/TableView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useFilterStore } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { noop } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useMemo } from "react";
import { jobsQueryOptions } from "~/components/build/Show/lib/queries/jobs";

export const Table = ({ className }) => {
  const { build } = useBuild();
  const filters = useFilterStore((state) => state.filterBy);
  const tableSearchValue = useBuildStore((state) => state.tableSearchValue);
  const showRetriedJobs = useBuildPreferencesStore((state) => state.showRetriedJobs);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage } =
    useInfiniteQuery(
      jobsQueryOptions(build, filters, showRetriedJobs, tableSearchValue, {
        placeholderData: keepPreviousData,
        enabled: Features.ProgressiveBuildShow,
        staleTime: 1000 * 30, // 30 seconds
      }),
    );

  const jobs = useMemo(() => data?.pages.flatMap((page) => page.records) ?? [], [data]);

  return Features.ProgressiveBuildShow ? (
    <TableView
      jobs={jobs}
      filters={filters}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      className={className}
    />
  ) : (
    <TableView
      jobs={build.jobs}
      filters={filters}
      hasNextPage={false}
      isLoading={false}
      isFetching={false}
      isFetchingNextPage={false}
      fetchNextPage={noop}
      className={className}
    />
  );
};

export default function TablePage() {
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorView className="px-4 py-2" clearError={() => navigate(0)} />}
    >
      <div
        className="relative min-w-0 flex-auto rounded-b-md bg-white contain-layout"
        data-testid="table"
      >
        <Table className="absolute" />
      </div>
    </ErrorBoundary>
  );
}
